import {setTimezone} from "@/src/modules/core/lib";
import {
  normalizePropertyAddressFields,
  normalizePropertyAddressForWrite,
} from "@/src/modules/properties/lib/property-address";

import {meService} from "../../core/services/me";
import {companyApi} from "../api";
import {useCompanyStore} from "../store";
import {
  Company,
  CompanyConfig,
  CompanyConfigForCreate,
  CompanyDetail,
  CompanyDetailForCreate,
  CompanyDetailType,
  CompanyRequest,
} from "../types";

function normalizeCompany(company: Company): Company {
  return normalizePropertyAddressFields(company) as Company;
}

function extractListData<T>(res: {data?: T[] | {data?: T[]}; [k: string]: unknown}): T[] {
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === "object" && "data" in d && Array.isArray((d as {data?: T[]}).data)) {
    return (d as {data: T[]}).data;
  }
  return [];
}

class CompanyService {
  async getCompany() {
    const {
      data: {data: company},
    } = await companyApi.getCompany();

    const normalizedCompany = normalizeCompany(company);

    useCompanyStore.setState({
      company: normalizedCompany,
    });
    setTimezone(normalizedCompany.timezone);

    return normalizedCompany;
  }

  async updateCompany(data: CompanyRequest) {
    const company = useCompanyStore.getState().company;
    const payload: CompanyRequest = {
      ...data,
      ...normalizePropertyAddressForWrite({
        street: data.street,
        number: data.number,
        unit_number: data.unit_number,
        city: data.city,
        state: data.state,
        zipcode: data.zipcode,
      }),
    };

    // Optimistic update
    if (company) {
      useCompanyStore.setState({
        company: normalizeCompany({
          ...company,
          ...payload,
          configs: payload.configs.map((config, index) => {
            const existingConfig = company.configs[index];
            return {
              ...config,
              company_config_id: config.company_config_id ?? existingConfig?.company_config_id ?? 0,
              created_at: existingConfig?.created_at ?? new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as CompanyConfig;
          }),
        }),
      });
    }

    try {
      const {
        data: {data: updatedCompany},
      } = await companyApi.updateCompany(payload);

      useCompanyStore.setState({
        company: normalizeCompany(updatedCompany),
      });

      return normalizeCompany(updatedCompany);
    } catch (e) {
      // Revert on error
      if (company) {
        useCompanyStore.setState({
          company,
        });
      }
      throw e;
    }
  }

  getConfig(category: string, configKey: string) {
    const company = useCompanyStore.getState().company;
    return company?.configs.find(
      (config: CompanyConfig) => config.category === category && config.config_key === configKey,
    );
  }

  getRestBreakMinDuration() {
    return (this.getConfig("break", "rest_break_duration")?.config_value as number) ?? 0;
  }

  getMealBreakMinDuration() {
    return (this.getConfig("break", "meal_break_duration")?.config_value as number) ?? 0;
  }

  getBreaksFlexibleRoles() {
    return (this.getConfig("break", "flexible_break_roles")?.config_value as string[]) ?? [];
  }

  getRolesWithoutBreaks() {
    return (this.getConfig("break", "roles_without_breaks")?.config_value ?? []) as number[];
  }

  async getConfigs(companyId: number): Promise<CompanyConfig[]> {
    const {data} = await companyApi.getConfigs(companyId);
    return extractListData<CompanyConfig>(data as {data?: CompanyConfig[]});
  }

  async createConfig(
    companyId: number,
    payload: CompanyConfigForCreate,
  ): Promise<CompanyConfig> {
    const {data} = await companyApi.createConfig(companyId, payload);
    return (data as {data: CompanyConfig}).data;
  }

  async updateConfig(
    companyId: number,
    configId: number,
    payload: CompanyConfigForCreate,
  ): Promise<CompanyConfig> {
    const {data} = await companyApi.updateConfig(companyId, configId, payload);
    return (data as {data: CompanyConfig}).data;
  }

  async deleteConfig(companyId: number, configId: number): Promise<void> {
    await companyApi.deleteConfig(companyId, configId);
  }

  async getDetails(companyId: number): Promise<CompanyDetail[]> {
    const {data} = await companyApi.getDetails(companyId);
    return extractListData<CompanyDetail>(data as {data?: CompanyDetail[]});
  }

  async createDetail(
    companyId: number,
    payload: CompanyDetailForCreate,
  ): Promise<CompanyDetail> {
    const {data} = await companyApi.createDetail(companyId, payload);
    return (data as {data: CompanyDetail}).data;
  }

  async updateDetail(
    companyId: number,
    detailId: number,
    payload: CompanyDetailForCreate,
  ): Promise<CompanyDetail> {
    const {data} = await companyApi.updateDetail(companyId, detailId, payload);
    return (data as {data: CompanyDetail}).data;
  }

  async deleteDetail(companyId: number, detailId: number): Promise<void> {
    await companyApi.deleteDetail(companyId, detailId);
  }

  getIsUpdateEnabled(): boolean {
    return meService.getHasPermission("company update");
  }

  async getCompanyDetailTypes(): Promise<CompanyDetailType[]> {
    const {data} = await companyApi.getCompanyDetailTypes();
    const body = data as {
      company_detail_types?: CompanyDetailType[];
      data?: {company_detail_types?: CompanyDetailType[]};
    };
    const raw =
      body.company_detail_types ??
      body.data?.company_detail_types;
    return Array.isArray(raw) ? raw : [];
  }
}

export const companyService = new CompanyService();
